import React, { useEffect, useMemo, useRef } from 'react'
import {
  Modal,
  ModalContent,
  ModalOverlay,
  ModalCloseButton
} from 'src/shared/components/common/modal/Modal'
import { t } from 'src/translations'
import { useModal } from 'src/shared/components/common/modal'
import { useLocation } from 'react-router'
import useIsClient from 'shared/js/hooks/useIsClient'
import { useOffers } from 'src/public/components/online_ordering/OffersContext'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import Image from 'shared/components/common/Image'
import {
  LandingConfig,
  parseLandingConfig,
  eligibleForLandingUX,
  configContainsOffer,
  configContainsSavings,
  getOfferExpiryText,
  formatSavingsAmount
} from './landingUtils'
import { BrandedButton } from 'src/shared/components/common/button'
import { ButtonType } from 'src/apollo/sites'
import { SavingsIcon } from './SavingsIcon'
import { AddIcon } from '@toasttab/buffet-pui-icons'
import { StickerIllustration } from '../../offers/OffersIcons'
import { CopyPromoCodeButton } from '../../offers/Offers'
import { DiscountType } from '../../offers/types'
import { useThemeColorScheme } from '../../meta/useTheme'
import { DEFAULT_COLORS } from '@toasttab/sites-components'
import { RankedPromoOfferDiscount } from 'src/apollo/onlineOrdering'
import { useCart } from 'src/public/components/online_ordering/CartContext'
import Skeleton from 'react-loading-skeleton'
import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'

type SavingsDisplayProps = {
  config: LandingConfig
}

const SavingsDisplay = ({ config }: SavingsDisplayProps) => {
  const { ooRestaurant } = useRestaurant()

  const savingsAmount = useMemo(
    () => formatSavingsAmount(config, ooRestaurant?.i18n.locale),
    [config, ooRestaurant?.i18n.locale]
  )

  if (!savingsAmount) return null

  return (
    <div className='savingsContainer'>
      <div className='savingsHeader'>{t('qr-modal.savings.heading')}</div>
      <div className='savingsIndicator'>
        <SavingsIcon />
        <div className='savingsAmount'>
          <div className='amount'>{savingsAmount}</div>
          <div className='label'>{t('qr-modal.savings.totalSavings')}</div>
        </div>
        <SavingsIcon />
      </div>
    </div>
  )
}

type OfferDisplayProps = {
  offer: RankedPromoOfferDiscount
}

const OfferDisplay = ({ offer }: OfferDisplayProps) => {
  const { restaurant } = useRestaurant()
  const getColorFromTheme = useThemeColorScheme()
  const { cart } = useCart()
  const appliedDiscountCode =
    cart?.order?.discounts?.restaurantDiscount?.promoCode

  const illustrationColor = useMemo(() => {
    return getColorFromTheme(
      (theme) =>
        theme.colorOverrides?.offerColorOverrides?.illustration ??
        theme.colorScheme.background.primary,
      restaurant.meta.primaryColor ?? DEFAULT_COLORS.primary
    )
  }, [getColorFromTheme, restaurant.meta.primaryColor])

  const discountType =
    offer.discount?.amountType === 'FIXED' ||
    offer.discount?.bogoAction?.amountType === 'FIXED'
      ? DiscountType.DOLLAR
      : DiscountType.PERCENT

  return (
    <div className='offerContainer' data-testid='offer-container'>
      <div className='illustration'>
        <StickerIllustration
          color={illustrationColor}
          discountType={discountType}
        />
      </div>
      <div className='offerDetails'>
        <div className='offerText'>{offer.bannerText}</div>
        <div className='offerApplication'>
          {appliedDiscountCode === offer.promoCode
            ? t('offers.applied')
            : t('offers.apply-at-checkout')}
        </div>
      </div>
      {offer.promoCode && <CopyPromoCodeButton promoCode={offer.promoCode} />}
    </div>
  )
}

const OfferSkeletonDisplay = () => {
  return (
    <div className='offerContainer' data-testid='offer-skeleton'>
      <div className='illustration'>
        <Skeleton circle width={54} height={54} />
      </div>
      <div className='offerDetails'>
        <div className='offerText'>
          <Skeleton width={120} height={18} />
        </div>
        <div className='offerApplication'>
          <Skeleton width={120} height={14} />
        </div>
      </div>
    </div>
  )
}

type QRCodeLandingViewProps = {
  config: LandingConfig
  onClose: () => void
  hasTrackedRef: React.MutableRefObject<boolean>
}

const QRCodeLandingView = ({
  config,
  onClose,
  hasTrackedRef
}: QRCodeLandingViewProps) => {
  const { offerFromUrl, loading: offersLoading } = useOffers()
  const { restaurant, ooRestaurant } = useRestaurant()
  const { icon, iconObject } = restaurant.meta
  const rxName = ooRestaurant?.name || ''
  const { track } = useThrottledTracker()

  const showSavings = configContainsSavings(config)
  const hasOfferInConfig = configContainsOffer(config)

  const offerPromoCodeMatches =
    hasOfferInConfig && offerFromUrl?.promoCode === config.promoCode

  const showOffer = !offersLoading && offerPromoCodeMatches && offerFromUrl

  const showOfferSkeleton = offersLoading && hasOfferInConfig

  const offerExpiryText = useMemo(
    () => getOfferExpiryText(offerFromUrl, ooRestaurant?.i18n.locale),
    [offerFromUrl, ooRestaurant?.i18n.locale]
  )

  // Track QR landing modal view once async data is ready
  useEffect(() => {
    if (hasTrackedRef.current) return

    // Wait for offers to finish loading if config contains an offer
    const payloadReady = !hasOfferInConfig || !offersLoading
    if (!payloadReady) return

    const offerProperty =
      hasOfferInConfig && offerPromoCodeMatches
        ? offerFromUrl?.promoCode
        : hasOfferInConfig
        ? 'not-found'
        : 'not-included'

    hasTrackedRef.current = true
    track(TrackingEvents.QR_LANDING_MODAL_VIEWED, {
      savingsVisible: showSavings,
      offerVisible: offerProperty
    })
  }, [
    showSavings,
    hasOfferInConfig,
    offersLoading,
    offerPromoCodeMatches,
    track,
    hasTrackedRef,
    offerFromUrl?.promoCode
  ])

  return (
    <div>
      <div className='landingHeader'>
        <ModalCloseButton onClose={onClose} />
      </div>
      <div className='landingBody'>
        <div className='logoSection'>
          <Image
            src={icon}
            imageObject={iconObject}
            alt={rxName}
            className='logo'
          />
          <div className='name'>{rxName}</div>
        </div>
        <div className='headerSection'>
          <h1>{t('qr-modal.headline')}</h1>
          <div>{t('qr-modal.subheading')}</div>
        </div>
        <div className='summarySection'>
          {showSavings && <SavingsDisplay config={config} />}
          {showSavings && (showOffer || showOfferSkeleton) && (
            <div className='plusSeparator' data-testid='plus-separator'>
              <AddIcon accessibility='decorative' />
            </div>
          )}
          {showOfferSkeleton && <OfferSkeletonDisplay />}
          {showOffer && <OfferDisplay offer={offerFromUrl} />}
          {showOffer && offerExpiryText && (
            <div className='offerExpiry'>
              {t('qr-modal.offer.expires', { date: offerExpiryText })}
            </div>
          )}
        </div>
        <BrandedButton
          variant={ButtonType.Primary}
          onClick={onClose}
          testId='start-order-btn'
          className='startOrderBtn'
        >
          {showSavings
            ? t('qr-modal.explore-savings')
            : t('qr-modal.explore-menu')}
        </BrandedButton>
      </div>
    </div>
  )
}

const QRCodeLandingModal = () => {
  const isClient = useIsClient()
  const { isOpen, onOpen, onClose } = useModal()
  const { search } = useLocation()
  // Ref lives in parent to remain stable and prevent duplicate tracking if children remount (QRCodeLandingView)
  const hasTrackedRef = useRef(false)

  const [landingConfig, shouldOpen] = useMemo(() => {
    const receiptCtxParam = new URLSearchParams(search).get('receiptCtx')
    const landingConfig = parseLandingConfig(receiptCtxParam)
    const shouldOpen = eligibleForLandingUX(landingConfig)
    return [landingConfig, shouldOpen]
  }, [search])

  useEffect(() => {
    // Only open if eligible AND we haven't shown it yet
    if (shouldOpen) {
      onOpen()
      // Remove receiptCtx from URL after opening
      const url = new URL(window.location.href)
      url.searchParams.delete('receiptCtx')
      window.history.replaceState(null, '', url.toString())
    }
  }, [shouldOpen, onOpen])

  if (!isClient || !landingConfig) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} testId='qrcode-landing-modal'>
      <ModalOverlay />
      <ModalContent contentClassName='modalContentV2 bottomDrawerMobile qrCodeLandingModal'>
        <QRCodeLandingView
          config={landingConfig}
          onClose={onClose}
          hasTrackedRef={hasTrackedRef}
        />
      </ModalContent>
    </Modal>
  )
}

export default React.memo(QRCodeLandingModal)
